/**
 * Formatting shared across screens.
 *
 * Prices are cents everywhere on the wire (Firestore/v1) — this is the one
 * place that turns cents into a dollar string. There is no client-side
 * option union to interpret anymore: `repositories/partners.js#activeServices`
 * (server) already resolves each option's `amountCents` and `label`, so the
 * `typeof opt === 'number'` guessing game the four donor screens used to
 * repeat (per-unit vs flat, inferred from the option's own JS type) is gone —
 * an option is always `{ id, label, amountCents, isPerUnit }`.
 */

/** `amountCents` in, a dollar string out. */
export const formatAmount = (amountCents) => `$${(Number(amountCents || 0) / 100).toLocaleString('en-US')}`;

/** Counts of 1000+ render as `1.4k+`, as on the app's cards. */
export const formatCount = (count) =>
  count >= 1000 ? `${(count / 1000).toFixed(1)}k+` : String(count ?? 0);

/**
 * Progressive phone formatting for the claim form. Not a validator and not a
 * normaliser — it only groups the digits a claimant types so a long number
 * stays readable while they enter it. NANP numbers (dial code `+1`) get the
 * familiar `(555) 123-4567`; every other country falls back to even three-digit
 * groups, which is not strictly correct anywhere but is readable everywhere.
 * The country dial code is chosen separately in the form, so only the national
 * part is handled here.
 */
export const formatPhoneNumber = (input, dialCode = '') => {
  const digits = String(input || '').replace(/\D/g, '').slice(0, 15);
  if (!digits) return '';
  if (dialCode === '+1') {
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6, 10);
    if (digits.length <= 3) return area;
    if (digits.length <= 6) return `(${area}) ${prefix}`;
    return `(${area}) ${prefix}-${line}`;
  }
  // Everywhere else: 3 – 3 – rest. Not the correct grouping for any one
  // country, but it keeps an 8–10 digit number (what the listed countries
  // actually use) readable without a per-country table.
  return [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6)].filter(Boolean).join(' ');
};

export const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * A `YYYY-MM-DD` calendar date, formatted without ever touching a timezone —
 * `new Date('2027-02-17')` is UTC midnight and can render as the 16th west of
 * Greenwich, which an event date cannot tolerate. Parts are split by hand.
 */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const parseYmd = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  return m ? { y: +m[1], mo: +m[2] - 1, d: +m[3] } : null;
};

/** One event's date or date range: "17 February 2027", "17–19 February 2027",
 * "28 February – 2 March 2027". */
export const formatEventDateRange = (startDate, endDate) => {
  const a = parseYmd(startDate);
  if (!a) return startDate || '';
  const b = endDate ? parseYmd(endDate) : null;
  if (!b || (a.y === b.y && a.mo === b.mo && a.d === b.d)) {
    return `${a.d} ${MONTHS[a.mo]} ${a.y}`;
  }
  if (a.y === b.y && a.mo === b.mo) {
    return `${a.d}–${b.d} ${MONTHS[a.mo]} ${a.y}`;
  }
  if (a.y === b.y) {
    return `${a.d} ${MONTHS[a.mo]} – ${b.d} ${MONTHS[b.mo]} ${a.y}`;
  }
  return `${a.d} ${MONTHS[a.mo]} ${a.y} – ${b.d} ${MONTHS[b.mo]} ${b.y}`;
};

/** Compact form for a card chip: "17 Feb", "17–19 Feb". */
export const formatEventDateShort = (startDate, endDate) => {
  const a = parseYmd(startDate);
  if (!a) return startDate || '';
  const b = endDate ? parseYmd(endDate) : null;
  const mon = (i) => MONTHS[i].slice(0, 3);
  if (!b || (a.mo === b.mo && a.d === b.d && a.y === b.y)) return `${a.d} ${mon(a.mo)}`;
  if (a.mo === b.mo && a.y === b.y) return `${a.d}–${b.d} ${mon(a.mo)}`;
  return `${a.d} ${mon(a.mo)} – ${b.d} ${mon(b.mo)}`;
};

/**
 * Tibetan script detection — U+0F00–U+0FFF.
 *
 * Reuse this rather than writing another regex; the app makes the same point.
 * Text that matches is set larger with a taller line box, because Tibetan
 * stacks vowel marks above and below the baseline and the default line box
 * clips them.
 */
export const containsTibetan = (text) => /[ༀ-࿿]/.test(text || '');
